import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, auth } from './client';
import { clerkSignOut, hasClerkSession, HUB_URL } from '@/lib/clerkSso';
import { counter } from '@/lib/counterMode';
import type { User, UserRole } from '@/types';

interface LoginResponse {
  token: string;
  /**
   * Délivré par le backend sur `/login`, `/team-login` et `/register`. Absent de
   * ce type jusqu'ici, donc jeté — d'où l'éjection vers `/login` à l'expiration
   * du jeton d'accès. Optionnel par prudence : un backend plus ancien peut ne
   * pas l'envoyer, et on ne veut pas casser la connexion pour autant.
   */
  refreshToken?: string;
  user: User;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/api/auth/login', { email, password }, { skipAuth: true }),

  teamLogin: (pharmacyId: string, pin: string) =>
    api.post<LoginResponse>('/api/auth/team-login', { pharmacyId, pin }, { skipAuth: true }),

  me: () => api.get<{ user: User }>('/api/auth/me'),

  /** Installe le mode comptoir sur CETTE machine (titulaire connecté). */
  openCounter: () =>
    api.post<{ token: string; user: User }>('/api/auth/counter'),

  /** Le PIN du titulaire rend la session de gestion, sur un poste comptoir. */
  unlock: (pin: string) =>
    api.post<{ token: string; expiresIn: number; user: User }>('/api/auth/unlock', { pin }),

  logout: () => api.post('/api/auth/logout'),

  register: (data: {
    pharmacyName: string;
    shuttleEmail: string;
    teamPin: string;
    address?: string;
    ownerFirstName: string;
    ownerLastName: string;
    ownerEmail: string;
    ownerPassword: string;
    /**
     * ⚠️ Ne renvoie plus de jeton : l'accès est conditionné à la confirmation
     * de l'adresse. L'écran d'inscription mène donc à « vérifiez votre boîte
     * mail », pas au tableau de bord.
     */
  }) =>
    api.post<{
      pharmacy: { id: string; name: string };
      user: { id: string; email: string | null; role: UserRole };
      emailVerificationRequired: true;
      message: string;
    }>('/api/pharmacy/register', data, { skipAuth: true }),

  verifyEmail: (token: string) =>
    api.post<{ success: true; alreadyVerified: boolean; email: string | null }>(
      '/api/auth/verify-email',
      { token },
      { skipAuth: true }
    ),

  resendVerification: (email: string) =>
    api.post<{ success: true; message: string }>(
      '/api/auth/resend-verification',
      { email },
      { skipAuth: true }
    ),

  forgotPassword: (email: string) =>
    api.post<{ success: true; message: string }>(
      '/api/auth/forgot-password',
      { email },
      { skipAuth: true }
    ),

  /** Valide le lien sans le consommer. 410 = expiré ou déjà utilisé. */
  checkResetToken: (token: string) =>
    api.get<{ valid: true; email: string }>(
      `/api/auth/reset-password/${encodeURIComponent(token)}`,
      { skipAuth: true }
    ),

  /**
   * Première connexion depuis PharmaGestion : cette identité a-t-elle déjà une
   * pharmacie ? Authentifié par le jeton Clerk, pas par un compte Officina.
   */
  onboardingStatus: () =>
    api.get<{
      email: string;
      needsOnboarding: boolean;
      pharmacy: { id: string; name: string } | null;
      /** LOT_ROLES_HUB : équipe PharmaGestion ou commercial (voir api/platform.ts). */
      platform: import('./platform').EtatPlateforme | null;
    }>('/api/pharmacy/onboarding-status'),

  onboard: (data: {
    pharmacyName: string;
    shuttleEmail: string;
    address?: string;
    teamPin: string;
  }) =>
    api.post<{
      pharmacy: { id: string; name: string };
      user: { id: string; email: string; role: UserRole };
    }>('/api/pharmacy/onboard', data),

  resetPassword: (token: string, password: string) =>
    api.post<{ success: true; message: string }>(
      '/api/auth/reset-password',
      { token, password },
      { skipAuth: true }
    ),
};

export function useResendVerification() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => authApi.resendVerification(email),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: ({ email }: { email: string }) => authApi.forgotPassword(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authApi.resetPassword(token, password),
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      if (!auth.isAuthenticated()) return null;
      try {
        const { user } = await authApi.me();
        return user;
      } catch {
        return null;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: ({ token, refreshToken, user }) => {
      auth.setToken(token);
      if (refreshToken) auth.setRefreshToken(refreshToken);
      // Nouvelle session : on repart de la pharmacie du jeton, jamais de
      // l'officine multi-site laissée par la session précédente.
      auth.clearSite();
      qc.setQueryData(['auth', 'me'], user);
      // La réponse de /login n'est qu'un portrait : les options payantes de
      // l'officine (onglet « Gestion Sociale »…) ne viennent que de /me.
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useTeamLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pharmacyId, pin }: { pharmacyId: string; pin: string }) =>
      authApi.teamLogin(pharmacyId, pin),
    onSuccess: ({ token, refreshToken, user }) => {
      auth.setToken(token);
      if (refreshToken) auth.setRefreshToken(refreshToken);
      auth.clearSite();
      qc.setQueryData(['auth', 'me'], user);
    },
  });
}

/**
 * Transforme cet ordinateur en poste comptoir.
 *
 * On efface tout ce qui reste de la session personnelle du titulaire : jeton,
 * refresh token, et **la session Clerk** s'il est venu par PharmaGestion. Sans
 * ce dernier point, le hub réouvrirait sa session pleine sur la machine du
 * comptoir au premier rechargement, et le mode ne protégerait rien.
 */
/**
 * VERROUILLER / DÉVERROUILLER RECHARGE LA PAGE — et c'est voulu.
 *
 * Le premier essai se contentait de vider le cache React Query et de réécrire
 * l'utilisateur courant. Insuffisant : il fallait un F5 pour que l'interface
 * change vraiment. Ce n'est pas un simple cache à invalider — TOUT change en
 * même temps : le jeton présenté par `api`, le rôle qui choisit le cadre, ce que
 * le serveur accepte de servir, et l'officine active en multi-site. Un écran qui
 * a commencé son rendu d'un côté de cette bascule ne doit pas le finir de
 * l'autre.
 *
 * Un rechargement est aussi ce que fait déjà la déconnexion, et il a une vertu
 * propre au comptoir : plus rien de la session du titulaire ne survit en
 * mémoire quand l'écran retourne à l'équipe.
 */
function reloadInto(path?: string) {
  // L'officine active voyage à part du jeton (`X-Site-Id`). Un site choisi
  // pendant la session de gestion resterait sinon en travers : le comptoir
  // l'ignore (le serveur refuse l'en-tête à un rôle non-titulaire), mais le
  // déverrouillage SUIVANT le reprendrait, et la gestion se rouvrirait sur une
  // autre officine que celle affichée. Un poste comptoir vaut pour SA pharmacie.
  auth.clearSite();
  if (path) window.location.assign(path);
  else window.location.reload();
}

export function useOpenCounter() {
  return useMutation({
    mutationFn: async () => {
      const res = await authApi.openCounter();
      // ORDRE CRITIQUE — le poste s'installe d'abord.
      //
      // `Clerk.signOut()` peut naviguer de lui-même (le hub renvoie sur sa page
      // de connexion). S'il partait avant, le jeton du poste ne serait jamais
      // écrit : la machine se retrouverait déconnectée, sans session et sans
      // poste, c'est-à-dire dans l'état exact que ce mode doit éviter.
      auth.clearToken();
      counter.install(res.token);
      // Ensuite seulement, on ferme la session du hub : sur une machine
      // partagée, la laisser ouverte permettrait à quiconque de retourner sur
      // PharmaGestion sous l'identité du titulaire.
      await clerkSignOut();
      return res;
    },
    // Le poste s'ouvre sur l'emploi du temps : c'est l'écran qu'on regarde le
    // plus au comptoir, et `/settings` d'où l'on vient lui est fermé.
    onSuccess: () => reloadInto('/planning'),
  });
}

/** Le PIN ouvre la gestion sur un poste comptoir, pour 30 minutes. */
export function useUnlockCounter() {
  return useMutation({
    mutationFn: ({ pin }: { pin: string }) => authApi.unlock(pin),
    onSuccess: ({ token, expiresIn }) => {
      counter.setUnlocked(token, expiresIn);
      // Rechargement sur place : le titulaire reste sur l'écran qu'il
      // regardait, il l'a simplement en entier maintenant.
      reloadInto();
    },
  });
}

/** Referme la gestion et rend l'écran à l'équipe. */
export function useLockCounter() {
  return () => {
    counter.lock();
    reloadInto('/planning');
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    // Se déconnecter ne vidait que le navigateur : les refresh tokens restaient
    // VALIDES côté serveur, donc récupérables sur un poste partagé. La route
    // les révoque tous — elle a besoin du jeton, donc elle passe AVANT
    // `clearToken()`. Son échec (réseau coupé) ne doit pas retenir quelqu'un
    // qui veut partir : on nettoie et on sort quand même.
    try {
      await authApi.logout();
    } catch {
      // Déconnexion locale malgré tout.
    }
    // Où renvoyer dépend de PAR OÙ la personne est entrée, pas du domaine.
    //
    // Un TITULAIRE vient de PharmaGestion : sa session est portée par Clerk, et
    // se déconnecter d'Officina veut dire revenir à la maison mère, d'où il
    // choisit son application (RH, Compta, Collect…). Il faut donc aussi fermer
    // la session Clerk, sinon il se croit dehors et revient connecté au premier
    // rechargement.
    //
    // Un SALARIÉ n'a pas de compte PharmaGestion : il se connecte par mot de
    // passe. L'envoyer sur le hub le déposerait devant une porte qui n'est pas
    // la sienne, sans moyen de revenir. Il retourne à l'écran de connexion.
    const viaClerk = hasClerkSession();
    if (viaClerk) await clerkSignOut();

    auth.clearToken();
    qc.clear();
    window.location.href = viaClerk ? HUB_URL : '/login';
  };
}

export function canAccess(user: User | null | undefined, roles: UserRole[]): boolean {
  if (!user) return false;
  return roles.includes(user.role);
}
